<template>
  <div class="container relative flex flex-col w-full h-full overflow-hidden">
    <div class="header" />
    <div class="relative mt-4 w-full h-full overflow-y-auto">
      <section>
        <h2 v-if="showBothArticleAndModelDO" class="text-lg font-semibold mb-4">
          {{ t('designOption.modelDesignOptions') }}
        </h2>
        <div v-if="designOptions.length" class="max-h-120 overflow-y-auto pr-2">
          <div class="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            <div
              v-for="designOption in designOptions"
              :key="designOption.Id"
              class="flex flex-col items-center p-4 shadow-card rounded-lg hover:shadow-lg transition-all duration-200 cursor-pointer hover:bg-gray-100"
              @click="onDesignOptionClick(designOption)"
            >
              <img
                :src="getAssetUrl(designOption)"
                :alt="designOption.FileName"
                class="w-24 h-24 object-scale-down"
              >
              <div class="w-full flex justify-end z-10" @click.stop>
                <tx-actions-button
                  :items="[{
                    key: 'delete',
                    label: 'Delete',
                    icon: '',
                    visible: userStore.userProfile.isValidPrivilege(privileges.duneImage.duneImageReadWritePrivilege),
                    onClick: () => deleteDesignOption(designOption, false),
                  }]"
                />
              </div>
            </div>
          </div>
        </div>
        <div v-else class="flex items-center justify-center col-span-full">
          <p>{{ t('general.emptyList') }}</p>
        </div>
      </section>
      <section v-if="props.showBothArticleAndModelDO" class="mt-4">
        <h2 class="text-lg font-semibold mb-4">
          {{ t('designOption.articleDesignOptions') }}
        </h2>
        <div v-if="articleDesignOptions.length" class="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          <div
            v-for="designOption in articleDesignOptions"
            :key="designOption.Id"
            class="flex flex-col items-center p-4 shadow-card rounded-lg hover:shadow-lg transition-all duration-200 cursor-pointer hover:bg-gray-100"
            @click="onDesignOptionClick(designOption)"
          >
            <img
              :src="getAssetUrl(designOption)"
              :alt="designOption.FileName"
              class="w-24 h-24 object-scale-down"
            >
            <div class="w-full flex justify-end z-10" @click.stop>
              <tx-actions-button
                :items="[{
                  key: 'delete',
                  label: 'Delete',
                  icon: '',
                  visible: userStore.userProfile.isValidPrivilege(privileges.duneImage.duneImageReadWritePrivilege),
                  onClick: () => deleteDesignOption(designOption, true),
                }]"
              />
            </div>
          </div>
        </div>
        <div v-else class="flex items-center justify-center col-span-full">
          <p>{{ t('general.emptyList') }}</p>
        </div>
      </section>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useI18n } from 'vue-i18n'

import { ref, watch } from 'vue'
import type MyArticle from '@/models/myArticle'
import { useUserStore } from '@/store/userData'
import { useNotificationStore } from '@/store/notification'
import { useConfirmDialog } from '@/shared/composables/confirmDialog'
import appConfig from '@/services/appConfig'
import { changeAssetStatus, getDesignOptionAssets } from '@/api/dune/asset'
import type DuneAsset from '@/models/duneAsset'
import TxActionsButton from '@/shared/components/TxActionsButton.vue'
import { privileges } from '@/models/constants'
import utils from '@/services/utils'

interface IProps {
  article: MyArticle
  parentLoading?: boolean
  designOptionAdded?: string
  isArticleDesignOption?: boolean
  showBothArticleAndModelDO?: boolean
}

const props = withDefaults(defineProps<IProps>(), { isArticleDesignOption: false, showBothArticleAndModelDO: false })

const emit = defineEmits<{
  (e: 'designOptionsStatusUpdated', assetId: string, isArticleDesignOption: boolean): void
}>()

const { t } = useI18n()
const confirmDialog = useConfirmDialog()
const userStore = useUserStore()
const notificationStore = useNotificationStore()
const designOptions = ref<DuneAsset[]>([])
const articleDesignOptions = ref<DuneAsset[]>([])

// from article details
if (utils.isDefined(props.parentLoading) && !props.parentLoading) {
  onLoad()
  loadArticleDO()
}

function onDesignOptionClick(designOption: DuneAsset) {
  window.open(getAssetUrl(designOption))
}
async function deleteDesignOption(designOption: DuneAsset, isArticleDO = false) {
  if (userStore.activeCatalog) {
    await confirmDialog(t('general.alert'), t('designOption.confirmDeleteMessage'), [], async () => {
      await changeAssetStatus(userStore.activeCatalog!.DuneContext, userStore.activeCatalog!.ContextKey, [designOption.Id], false).then(() => {
        notificationStore.addNotification(({ message: t('designOption.alerts.deletedSuccessfully'), type: 'Success' }))
        emit('designOptionsStatusUpdated', designOption.Id, props.showBothArticleAndModelDO ? isArticleDO : props.isArticleDesignOption)
        const deletedAssetIndex = designOptions.value.findIndex(asset => asset.Id === designOption.Id)
        designOptions.value.splice(deletedAssetIndex, 1)
      }).catch(() => {
        notificationStore.addNotification(({ message: t('designOption.alerts.deletionFailed'), type: 'Alert' }))
      })
    })
  }
}
function getAssetUrl(asset: DuneAsset) {
  return `${appConfig.AssetsUrl}/assets/content/${asset.StorageFile}?ContextKey=${encodeURIComponent(userStore.activeCatalog!.ContextKey)}&f=webp&w=200&h=200&trim=true`
}
function loadArticleDO() {
  const catalogDetails = userStore.activeCatalog
  if (catalogDetails && props.article) {
    getDesignOptionAssets(catalogDetails.DuneContext, catalogDetails.ContextKey, props.article.ArticleNumber, false)
      .then((assets) => {
        articleDesignOptions.value = assets
      })
  }
}
function onLoad() {
  const catalogDetails = userStore.activeCatalog
  if (catalogDetails && props.article) {
    getDesignOptionAssets(catalogDetails.DuneContext, catalogDetails.ContextKey, utils.isDefined(props.isArticleDesignOption) && props.isArticleDesignOption ? props.article.ArticleNumber : props.article.ModelNumber, false)
      .then((assets) => {
        designOptions.value = assets
      })
  }
}
watch(() => props.article, () => {
  if (props.isArticleDesignOption) {
    onLoad()
  }
  if (props.showBothArticleAndModelDO) {
    loadArticleDO()
  }
})
watch(() => props.parentLoading, (val) => {
  if (val) {
    onLoad()
    if (props.showBothArticleAndModelDO) {
      loadArticleDO()
    }
  }
})
watch(() => props.designOptionAdded, (val) => {
  if (val) {
    onLoad()
    loadArticleDO()
  }
})
</script>
